import { Link, useParams, Navigate } from "react-router-dom";
import { Clock, Calendar, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import CTAStrip from "../components/CTAStrip";
import { ARTICLES } from "../data/articles";

export function ConseilsList() {
    return (
        <>
            <SEO
                title="Conseils — Portail, clôture, brise-vue & motorisation"
                description="Nos guides et conseils pour bien choisir votre portail, clôture, brise-vue ou motorisation. Expert autour de Genève."
                keywords={["conseil portail", "guide portail", "choisir portail", "portail aluminium", "motorisation portail"]}
            />
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-12">
                <div className="divider-tag mb-6">Conseils & Guides</div>
                <h1 className="font-display text-4xl md:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl">
                    Conseils d'expert<br />
                    <span className="italic text-white/80">pour bien choisir.</span>
                </h1>
                <p className="mt-6 text-white/65 max-w-3xl">
                    Comparatifs, prix, critères techniques : nos guides pour vous aider à faire les bons choix avant de lancer votre projet.
                </p>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
                <div className="grid md:grid-cols-2 gap-5">
                    {ARTICLES.map((a) => (
                        <Link key={a.slug} to={`/conseils/${a.slug}`} className="card-product block p-7 group" data-testid={`conseil-card-${a.slug}`}>
                            <div className="flex items-center gap-4 mb-4 text-[10px] uppercase tracking-[0.28em] text-brand-accent/80">
                                <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {a.date}</span>
                                <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {a.readTime}</span>
                            </div>
                            <h2 className="font-display text-2xl md:text-3xl text-white group-hover:text-brand-accent transition leading-tight">
                                {a.title}
                            </h2>
                            <p className="mt-3 text-sm text-white/60 leading-relaxed">{a.excerpt}</p>
                            <div className="mt-5 text-xs text-brand-accent link-underline inline-block">Lire l'article →</div>
                        </Link>
                    ))}
                </div>
            </section>

            <CTAStrip />
        </>
    );
}

export function ConseilArticle() {
    const { slug } = useParams();
    const article = ARTICLES.find((a) => a.slug === slug);
    if (!article) return <Navigate to="/conseils" replace />;

    return (
        <>
            <SEO title={article.seoTitle} description={article.seoDescription} />
            <section className="max-w-[920px] mx-auto px-6 lg:px-10 pt-16 pb-12">
                <Link to="/conseils" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-brand-accent mb-8">
                    <ArrowLeft className="w-4 h-4" /> Tous les conseils
                </Link>
                <div className="flex items-center gap-4 mb-6 text-[10px] uppercase tracking-[0.28em] text-brand-accent/80">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {article.date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {article.readTime}</span>
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-white leading-[1.05] tracking-tight">{article.title}</h1>
                <p className="mt-6 text-white/65 text-lg leading-relaxed">{article.excerpt}</p>
            </section>

            <article className="max-w-[760px] mx-auto px-6 lg:px-10 pb-20 prose-invert">
                {article.content.map((block, i) => {
                    if (block.type === "h2") {
                        return <h2 key={i} className="font-display text-2xl md:text-3xl text-white mt-10 mb-4">{block.text}</h2>;
                    }
                    return <p key={i} className="text-white/75 leading-[1.85] mb-5 text-[1.02rem]">{block.text}</p>;
                })}
            </article>

            <CTAStrip />
        </>
    );
}

export default ConseilsList;
